import { Box, FormControl, FormLabel } from "@mui/joy";
import DarkInput from "components/common/DarkInput";
import DarkCheckbox from "components/common/DarkCheckbox";
import { ColorPaletteSelect } from "components/common/ColorPalette";
import { hrAccentColor, textTokens } from "styles/globalStyles";
import { GROUP_DEFAULT_COLOR } from "utils/colorPalette";
import { TemplateFormBase } from "./TemplateFormBase";
import type { ParamsTemplateEntry } from "./paramsTemplateUtils";

/** Form for ExcursionGroupParamsInput: all fields from the type (swagger). */
export function GroupTemplateForm({
  name,
  description,
  color,
  maxUsersPerGroup,
  maxCustomersPerGroup,
  shouldBeUsedByDefault,
  requiresUserValidation,
  templateEntries,
  onNameChange,
  onDescriptionChange,
  onColorChange,
  onMaxUsersPerGroupChange,
  onMaxCustomersPerGroupChange,
  onShouldBeUsedByDefaultChange,
  onRequiresUserValidationChange,
  onTemplateEntriesAdd,
  onTemplateEntriesUpdate,
  onTemplateEntriesRemove,
  canEdit,
  saving,
  onSave,
  onDelete,
  showDelete,
}: {
  name: string;
  description: string;
  color: string;
  maxUsersPerGroup: string;
  maxCustomersPerGroup: string;
  shouldBeUsedByDefault: boolean;
  requiresUserValidation: boolean;
  templateEntries: ParamsTemplateEntry[];
  onNameChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onColorChange: (v: string) => void;
  onMaxUsersPerGroupChange: (v: string) => void;
  onMaxCustomersPerGroupChange: (v: string) => void;
  onShouldBeUsedByDefaultChange: (v: boolean) => void;
  onRequiresUserValidationChange: (v: boolean) => void;
  onTemplateEntriesAdd: () => void;
  onTemplateEntriesUpdate: (index: number, entry: ParamsTemplateEntry) => void;
  onTemplateEntriesRemove: (index: number) => void;
  canEdit: boolean;
  saving: boolean;
  onSave: () => void;
  onDelete: () => void;
  showDelete: boolean;
}) {
  const customFields = (
    <>
      <FormControl>
        <FormLabel sx={{ color: hrAccentColor }}>Couleur</FormLabel>
        <ColorPaletteSelect
          value={color || GROUP_DEFAULT_COLOR}
          onChange={onColorChange}
          disabled={!canEdit}
          defaultColor={GROUP_DEFAULT_COLOR}
          sx={{ minWidth: 140 }}
        />
      </FormControl>
      <Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <FormControl sx={{ minWidth: 0, flex: "1 1 120px" }}>
            <FormLabel sx={{ color: hrAccentColor }}>Max. encadrants/groupe</FormLabel>
            <DarkInput
              type="number"
              value={maxUsersPerGroup}
              onChange={(e) => onMaxUsersPerGroupChange(e.target.value)}
              disabled={!canEdit}
              slotProps={{ input: { sx: { color: textTokens.color.primary }, min: 0 } }}
            />
          </FormControl>
          <FormControl sx={{ minWidth: 0, flex: "1 1 120px" }}>
            <FormLabel sx={{ color: hrAccentColor }}>Max. clients/groupe</FormLabel>
            <DarkInput
              type="number"
              value={maxCustomersPerGroup}
              onChange={(e) => onMaxCustomersPerGroupChange(e.target.value)}
              disabled={!canEdit}
              slotProps={{ input: { sx: { color: textTokens.color.primary }, min: 0 } }}
            />
          </FormControl>
        </Box>
        <Box sx={{ mt: 1 }}>
          <DarkCheckbox
            label="Validation utilisateur requise (non employés)"
            checked={requiresUserValidation}
            onChange={(e) => onRequiresUserValidationChange(e.target.checked)}
            disabled={!canEdit}
          />
        </Box>
      </Box>
    </>
  );

  return (
    <TemplateFormBase
      name={name}
      description={description}
      shouldBeUsedByDefault={shouldBeUsedByDefault}
      onNameChange={onNameChange}
      onDescriptionChange={onDescriptionChange}
      onShouldBeUsedByDefaultChange={onShouldBeUsedByDefaultChange}
      templateEntries={templateEntries}
      onTemplateEntriesAdd={onTemplateEntriesAdd}
      onTemplateEntriesUpdate={onTemplateEntriesUpdate}
      onTemplateEntriesRemove={onTemplateEntriesRemove}
      canEdit={canEdit}
      saving={saving}
      onSave={onSave}
      onDelete={onDelete}
      showDelete={showDelete}
      customFields={customFields}
    />
  );
}
